import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../../../software/api";
import { refNo } from "../../../software/format";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { exportXLSX, exportPDF, printReport } from "./exportUtils";
import { DollarSign, AlertTriangle, TrendingUp, Wallet, ArrowRight, Users, FileText, RefreshCw, Printer, Mail, History, Package, Waves, FileSpreadsheet, FileDown } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money0 = (n) => `$${Number(n || 0).toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;
const todayISO = () => new Date().toLocaleDateString("en-CA");
const daysAgoISO = (d) => { const x = new Date(); x.setDate(x.getDate() - d); return x.toLocaleDateString("en-CA"); };

/* Faithful legacy report toolbar (Form_DebtorsList / SalesReport / PaymentsReceived):
   PRINT REPORT · CONVERT TO XLSX/PDF · EMAIL XLSX/PDF (owner: PDF + XLSX only). */
function ReportExportBar({ title, columns, rows, testid = "report" }) {
  const has = rows && rows.length;
  const guard = (fn) => () => { if (!has) { toast.error("No rows to export in this range."); return; } fn(); };
  const emailDraft = (fmt) => guard(() => {
    (fmt === "xlsx" ? exportXLSX : exportPDF)(title, columns, rows);
    toast.success(`${fmt.toUpperCase()} downloaded — attach it to the email draft.`);
    window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`Please find the ${title} attached (${fmt.toUpperCase()}).`)}`;
  })();
  const B = ({ tid, on, children, cls }) => (
    <button onClick={on} data-testid={tid} className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${cls}`}>{children}</button>
  );
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${testid}-export-bar`}>
      <span className="text-[11px] uppercase tracking-wider text-slate-400 mr-1">Export</span>
      <B tid={`${testid}-print`} on={guard(() => printReport(title, columns, rows))} cls="border-slate-300 text-slate-700 hover:bg-slate-100"><Printer className="h-3.5 w-3.5" /> Print Report</B>
      <B tid={`${testid}-xlsx`} on={guard(() => exportXLSX(title, columns, rows))} cls="border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"><FileSpreadsheet className="h-3.5 w-3.5" /> XLSX</B>
      <B tid={`${testid}-pdf`} on={guard(() => exportPDF(title, columns, rows))} cls="border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100"><FileDown className="h-3.5 w-3.5" /> PDF</B>
      <B tid={`${testid}-email-xlsx`} on={() => emailDraft("xlsx")} cls="border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100"><Mail className="h-3.5 w-3.5" /> Email XLSX</B>
      <B tid={`${testid}-email-pdf`} on={() => emailDraft("pdf")} cls="border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100"><Mail className="h-3.5 w-3.5" /> Email PDF</B>
    </div>
  );
}

/* Build & open a printable statement for one debtor, from already-loaded invoice rows */
function printStatement(clientName, invoices, branding) {
  const company = branding?.company || "Aqualux Nexus";
  const addr = [branding?.address, branding?.phone, branding?.email].filter(Boolean).join(" · ");
  const totalOwing = invoices.reduce((s, i) => s + (i.balance || 0), 0);
  const rows = invoices.map((i) => `<tr>
      <td>#${i.sale_number}</td><td>${String(i.date).slice(0, 10)}</td>
      <td style="text-align:right">${money(i.total)}</td>
      <td style="text-align:right">${money(i.paid)}</td>
      <td style="text-align:right;font-weight:700;color:#b91c1c">${money(i.balance)}</td>
    </tr>`).join("");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Statement - ${clientName}</title>
    <style>
      body{font-family:Arial,Helvetica,sans-serif;color:#1e293b;padding:40px;max-width:800px;margin:0 auto}
      h1{color:#203864;margin:0} .muted{color:#64748b;font-size:13px}
      .band{background:#203864;color:#fff;padding:16px 20px;border-radius:10px;margin:24px 0}
      table{width:100%;border-collapse:collapse;margin-top:16px;font-size:14px}
      th,td{padding:9px 10px;border-bottom:1px solid #e2e8f0;text-align:left}
      th{background:#f1f5f9;font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#64748b}
      .total{font-size:22px;font-weight:800;color:#b91c1c}
      .note{margin-top:24px;font-size:13px;color:#475569}
      @media print{button{display:none}}
    </style></head><body>
    <div style="display:flex;justify-content:space-between;align-items:flex-start">
      <div><h1>${company}</h1><p class="muted">${addr}</p></div>
      <div style="text-align:right"><h2 style="margin:0;color:#203864">STATEMENT</h2><p class="muted">${new Date().toLocaleDateString("en-AU")}</p></div>
    </div>
    <div class="band"><div style="font-size:12px;opacity:.8;text-transform:uppercase;letter-spacing:.1em">Account</div>
      <div style="font-size:20px;font-weight:700">${clientName}</div></div>
    <table><thead><tr><th>Invoice</th><th>Date</th><th style="text-align:right">Total</th><th style="text-align:right">Paid</th><th style="text-align:right">Owing</th></tr></thead>
      <tbody>${rows}</tbody></table>
    <p style="text-align:right;margin-top:18px" class="total">Total owing: ${money(totalOwing)}</p>
    <p class="note">Please arrange payment at your earliest convenience. If you have already paid, thank you — please disregard this statement.</p>
    <button onclick="window.print()" style="margin-top:20px;padding:10px 18px;background:#203864;color:#fff;border:none;border-radius:8px;font-weight:700;cursor:pointer">Print / Save PDF</button>
    </body></html>`;
  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.write(html); w.document.close();
  return true;
}

const VIEWS = [
  { id: "debtors", label: "Debtors & Outstanding", icon: AlertTriangle, tone: "rose" },
  { id: "sales", label: "Sales Report", icon: TrendingUp, tone: "emerald" },
  { id: "spa_sales", label: "Spa Sales History", icon: Waves, tone: "sky" },
  { id: "stock", label: "Stock Levels", icon: Package, tone: "amber" },
  { id: "payments", label: "Payments Received", icon: Wallet, tone: "sky" },
];

const Stat = ({ label, value, sub, tone = "slate", big }) => {
  const tones = { rose: "text-rose-700 bg-rose-50 border-rose-200", emerald: "text-emerald-700 bg-emerald-50 border-emerald-200", sky: "text-sky-700 bg-sky-50 border-sky-200", amber: "text-amber-700 bg-amber-50 border-amber-200", slate: "text-slate-700 bg-slate-50 border-slate-200" };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wider opacity-80">{label}</p>
      <p className={`font-display font-extrabold ${big ? "text-3xl" : "text-2xl"} mt-1 leading-none`}>{value}</p>
      {sub && <p className="text-[11px] opacity-70 mt-1">{sub}</p>}
    </div>
  );
};

/* ---------------- Debtors ---------------- */
function DebtorsReport({ onOpenClient, branding, scope = "all" }) {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("clients"); // clients | invoices
  const [includeLegacy, setIncludeLegacy] = useState(true);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => { setLoading(true); try { setData(await api("GET", `/reports/debtors?include_legacy=${includeLegacy}&scope=${scope}`)); } finally { setLoading(false); } }, [includeLegacy, scope]);
  useEffect(() => { load(); }, [load]);

  const doStatement = (clientId, clientName) => {
    const invs = (data.invoices || []).filter((i) => (i.client_id || i.client_name) === (clientId || clientName));
    if (!invs.length) { window.alert("No itemised invoices to build a statement from."); return; }
    if (!printStatement(clientName, invs, branding)) window.alert("Please allow pop-ups to print the statement.");
  };
  const doEmail = async (clientId, clientName) => {
    let email = "";
    try { if (clientId) { const c = await api("GET", `/clients/${clientId}`); email = c.email || ""; } } catch { /* ignore */ }
    const invs = (data.invoices || []).filter((i) => (i.client_id || i.client_name) === (clientId || clientName));
    const owing = invs.reduce((s, i) => s + (i.balance || 0), 0);
    const company = branding?.company || "our store";
    if (invs.length) {
      exportPDF(`Statement - ${clientName}`,
        [{ key: "sale_number", label: "Invoice" }, { key: "date2", label: "Date" }, { key: "total", label: "Total", money: true }, { key: "paid", label: "Paid", money: true }, { key: "balance", label: "Owing", money: true }],
        invs.map((i) => ({ ...i, date2: String(i.date).slice(0, 10) })));
      toast.success(`Statement PDF for ${clientName} downloaded — attach it to the draft.`);
    }
    const lines = invs.map((i) => `  Invoice #${i.sale_number} (${String(i.date).slice(0, 10)}): ${money(i.balance)} owing`).join("%0D%0A");
    const subject = encodeURIComponent(`Account statement from ${company}`);
    const body = `Hi ${clientName},%0D%0A%0D%0AThis is a friendly reminder of your account with ${company}.%0D%0A%0D%0A${lines}%0D%0A%0D%0ATotal owing: ${money(owing)}%0D%0A%0D%0APlease arrange payment at your convenience. If already paid, please disregard.%0D%0A%0D%0AThank you.`;
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  if (loading || !data) return <p className="text-slate-500 py-8 text-center" data-testid="debtors-loading">Loading debtors…</p>;
  const b = data.buckets || {};
  const legacy = data.legacy;
  return (
    <div data-testid="report-debtors" className="space-y-5">
      {data.scope && data.scope !== "all" && (
        <div className="flex items-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-4 py-2.5" data-testid="debtors-scope-banner">
          <span className="text-[11px] font-bold uppercase tracking-wider text-sky-500">Entity</span>
          <span className="text-sm font-bold text-sky-800">{data.scope_label}</span>
          <span className="ml-auto text-xs text-sky-600">{data.invoice_count} invoices · {money0(data.total_owed)} owing</span>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Total owed to you" value={money0(data.total_owed)} sub={`${data.invoice_count} invoices · ${data.client_count} clients`} tone="rose" big />
        <Stat label="Current (0-30 days)" value={money0(b.current)} tone="emerald" />
        <Stat label="31-60 days" value={money0(b.d31_60)} tone="amber" />
        <Stat label="61-90 days" value={money0(b.d61_90)} tone="amber" />
        <Stat label="90+ days overdue" value={money0(b.d90_plus)} tone="rose" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setMode("clients")} data-testid="debtors-mode-clients" className={`rounded-full px-4 py-1.5 text-sm font-semibold border ${mode === "clients" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300"}`}>By client</button>
        <button onClick={() => setMode("invoices")} data-testid="debtors-mode-invoices" className={`rounded-full px-4 py-1.5 text-sm font-semibold border ${mode === "invoices" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300"}`}>By invoice</button>
        <label className="ml-2 inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none" data-testid="debtors-legacy-toggle">
          <input type="checkbox" checked={includeLegacy} onChange={(e) => setIncludeLegacy(e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
          Include 15-yr legacy debt
        </label>
        {includeLegacy && data.legacy_summary && (
          <span className="text-[11px] text-amber-700 font-semibold" data-testid="debtors-legacy-note">
            includes {money0(data.legacy_summary.total_owed)} legacy across {data.legacy_summary.client_count} clients
          </span>
        )}
        <button onClick={load} className="ml-auto inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </div>

      <ReportExportBar testid="debtors" title={`Debtors Report - ${mode === "clients" ? "by client" : "by invoice"}`}
        columns={mode === "clients"
          ? [{ key: "client_name", label: "Client" }, { key: "invoices", label: "Invoices" }, { key: "oldest_days", label: "Oldest (days)" }, { key: "balance", label: "Balance", money: true }]
          : [{ key: "sale_number", label: "Invoice" }, { key: "client_name", label: "Client" }, { key: "total", label: "Total", money: true }, { key: "paid", label: "Paid", money: true }, { key: "balance", label: "Owing", money: true }, { key: "days", label: "Age (days)" }]}
        rows={mode === "clients" ? (data.clients || []) : (data.invoices || [])} />

      {data.total_owed === 0 ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="debtors-empty">
          <p className="font-display font-bold text-emerald-800 text-lg">🎉 Nobody owes you money right now</p>
          <p className="text-sm text-emerald-700 mt-1">Every live invoice is paid in full.</p>
        </div>
      ) : mode === "clients" ? (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {data.clients.map((c, i) => (
            <div key={c.client_id || i} data-testid={`debtor-client-${i}`} className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 hover:bg-slate-50 last:border-0">
              <button onClick={() => c.client_id && onOpenClient(c.client_id)} className="flex items-center gap-3 min-w-0 flex-1 text-left">
                <span className="h-9 w-9 rounded-lg bg-rose-50 border border-rose-100 text-rose-600 flex items-center justify-center shrink-0"><Users className="h-4 w-4" /></span>
                <span className="min-w-0"><span className="block font-semibold text-slate-900 truncate">{c.client_name}</span>
                  <span className="block text-[11px] text-slate-400">{c.invoices} invoice{c.invoices === 1 ? "" : "s"} · oldest {c.oldest_days} days</span></span>
              </button>
              <span className={`font-display font-extrabold ${c.oldest_days > 90 ? "text-rose-600" : "text-slate-900"}`}>{money(c.balance)}</span>
              <button onClick={() => doStatement(c.client_id, c.client_name)} data-testid={`debtor-statement-${i}`} title="Print / PDF statement" className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"><Printer className="h-3.5 w-3.5" /> Statement</button>
              <button onClick={() => doEmail(c.client_id, c.client_name)} data-testid={`debtor-email-${i}`} title="Email a chase reminder" className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-sky-300 bg-sky-50 px-2.5 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-100"><Mail className="h-3.5 w-3.5" /> Email</button>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400"><tr>
              <th className="text-left px-4 py-2.5">Invoice</th><th className="text-left px-4 py-2.5">Client</th>
              <th className="text-right px-4 py-2.5">Total</th><th className="text-right px-4 py-2.5">Paid</th>
              <th className="text-right px-4 py-2.5">Owing</th><th className="text-right px-4 py-2.5">Age</th></tr></thead>
            <tbody>
              {data.invoices.map((r, i) => (
                <tr key={r.sale_id || i} onClick={() => r.client_id && onOpenClient(r.client_id)} data-testid={`debtor-invoice-${i}`} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                  <td className="px-4 py-2.5 font-semibold text-slate-700">{refNo("invoice", r.sale_number)}</td>
                  <td className="px-4 py-2.5 text-slate-600 truncate max-w-[220px]">{r.client_name}</td>
                  <td className="px-4 py-2.5 text-right text-slate-500">{money(r.total)}</td>
                  <td className="px-4 py-2.5 text-right text-emerald-600">{money(r.paid)}</td>
                  <td className="px-4 py-2.5 text-right font-extrabold text-rose-600">{money(r.balance)}</td>
                  <td className={`px-4 py-2.5 text-right ${r.days > 90 ? "text-rose-600 font-bold" : "text-slate-400"}`}>{r.days}d</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {includeLegacy && legacy && (
        <div data-testid="debtors-legacy" className="rounded-2xl border border-amber-200 bg-amber-50/60 overflow-hidden">
          <div className="px-4 py-3 bg-amber-100/70 border-b border-amber-200 flex items-center gap-2">
            <History className="h-4 w-4 text-amber-700" />
            <span className="font-display font-bold text-amber-900">Legacy (pre-migration) outstanding — {money0(legacy.total_owed)} across {legacy.client_count} clients</span>
          </div>
          <p className="px-4 pt-2 text-[11px] text-amber-700">⚠ These come from your old Spa Buddy invoices and may have been settled outside the system. Treat as historical, verify before chasing.</p>
          <div className="max-h-80 overflow-y-auto">
            {legacy.clients.map((c, i) => (
              <button key={i} onClick={() => c.client_id && onOpenClient(c.client_id)} data-testid={`legacy-debtor-${i}`} className="w-full flex items-center gap-3 px-4 py-2.5 text-left border-b border-amber-100 hover:bg-amber-100/50 last:border-0">
                <span className="min-w-0 flex-1"><span className="block font-semibold text-slate-800 truncate">{c.client_name}</span>
                  <span className="block text-[11px] text-amber-600">{c.invoices} legacy invoice{c.invoices === 1 ? "" : "s"}</span></span>
                <span className="font-display font-bold text-amber-800">{money(c.balance)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Sales ---------------- */
function MonthlyChart({ data }) {
  const rows = data || [];
  if (!rows.length) return null;
  const max = Math.max(...rows.map((r) => r.total || 0), 1);
  const label = (m) => {
    const [y, mo] = String(m).split("-");
    return `${["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][Number(mo)] || mo} ${y}`;
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="sales-monthly-chart">
      <div className="flex items-center justify-between mb-3">
        <p className="font-display font-bold text-slate-900">Monthly takings</p>
        <p className="text-[11px] text-slate-400">{rows.length} months · POS + invoices</p>
      </div>
      <div className="overflow-x-auto pb-1">
        <div className="flex items-end gap-[3px] h-40 min-w-full" style={{ width: `${Math.max(rows.length * 11, 100)}px` }}>
          {rows.map((r) => {
            const h = Math.max((r.total / max) * 100, 1.5);
            const isYearStart = String(r.month).endsWith("-01");
            return (
              <div key={r.month} className="flex-1 min-w-[8px] h-full flex flex-col items-center justify-end group relative" title={`${label(r.month)} — ${money(r.total)}`} data-testid={`sales-month-${r.month}`}>
                <div className="w-full rounded-t-sm bg-emerald-500 group-hover:bg-emerald-600 transition-colors" style={{ height: `${h}%` }} />
                {isYearStart && <span className="absolute -bottom-4 text-[9px] text-slate-400 font-mono2 whitespace-nowrap">{String(r.month).slice(0, 4)}</span>}
              </div>
            );
          })}
        </div>
      </div>
      <div className="h-4" />
    </div>
  );
}

function SalesSummary() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = [];
      if (from) qs.push(`date_from=${from}T00:00:00`);
      if (to) qs.push(`date_to=${to}T23:59:59`);
      setData(await api("GET", `/reports/sales-summary${qs.length ? "?" + qs.join("&") : ""}`));
    }
    finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const quick = (d) => () => { setFrom(daysAgoISO(d)); setTo(todayISO()); };
  return (
    <div data-testid="report-sales" className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm text-slate-600">From<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="sales-from" className="block mt-1 rounded-lg border border-slate-300 px-3 py-2" /></label>
        <label className="text-sm text-slate-600">To<input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="sales-to" className="block mt-1 rounded-lg border border-slate-300 px-3 py-2" /></label>
        <div className="flex gap-1.5">
          <button onClick={() => { setFrom(""); setTo(""); }} data-testid="sales-all-time" className="rounded-full px-3 py-1.5 text-xs font-semibold bg-slate-800 text-white hover:bg-slate-900">All time</button>
          {[["Today", 0], ["7 days", 7], ["30 days", 30], ["90 days", 90]].map(([l, d]) => (
            <button key={l} onClick={quick(d)} className="rounded-full px-3 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700">{l}</button>
          ))}
        </div>
      </div>
      {loading || !data ? <p className="text-slate-500 py-8 text-center">Loading sales…</p> : (
        <>
          <ReportExportBar testid="sales-summary" title={`Sales Summary ${from} to ${to}`}
            columns={[{ key: "metric", label: "Metric" }, { key: "value", label: "Value" }]}
            rows={[
              { metric: "Total sales (POS + invoices)", value: money(data.combined_total) },
              { metric: "Counter (POS) sales", value: `${money(data.pos.total)} (${data.pos.count})` },
              { metric: "POS GST", value: money(data.pos.gst) },
              { metric: "Invoiced", value: `${money(data.invoices.total)} (${data.invoices.count})` },
              { metric: "Still outstanding", value: money(data.invoices.outstanding) },
              { metric: "Collected", value: money(data.invoices.paid) },
              ...Object.entries(data.by_method || {}).map(([m, v]) => ({ metric: `Payments · ${m}`, value: money(v) })),
              ...(data.top_products || []).map((p) => ({ metric: `Top product · ${p.name}`, value: `x${p.qty}  =  ${money(p.revenue)}` })),
            ]} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Total sales" value={money0(data.combined_total)} sub="POS + invoices" tone="emerald" big />
            <Stat label="Counter (POS)" value={money0(data.pos.total)} sub={`${data.pos.count} sales · GST ${money0(data.pos.gst)}`} tone="sky" />
            <Stat label="Invoiced" value={money0(data.invoices.total)} sub={`${data.invoices.count} invoices`} tone="slate" />
            <Stat label="Still outstanding" value={money0(data.invoices.outstanding)} sub={`${money0(data.invoices.paid)} collected`} tone="rose" />
          </div>
          <MonthlyChart data={data.monthly} />
          <div className="grid md:grid-cols-2 gap-5">
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="font-display font-bold text-slate-900 mb-3">Payments by method</p>
              {Object.keys(data.by_method || {}).length === 0 ? <p className="text-sm text-slate-400">No payments in range.</p> :
                Object.entries(data.by_method).map(([m, v]) => (
                  <div key={m} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                    <span className="capitalize text-slate-600">{m}</span><span className="font-semibold text-slate-900">{money(v)}</span>
                  </div>
                ))}
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="font-display font-bold text-slate-900 mb-3">Top products</p>
              {(data.top_products || []).length === 0 ? <p className="text-sm text-slate-400">No products sold in range.</p> :
                data.top_products.map((p, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                    <span className="text-slate-600 truncate max-w-[220px]">{p.name}</span>
                    <span className="text-slate-900"><span className="text-slate-400 text-xs mr-2">×{p.qty}</span>{money(p.revenue)}</span>
                  </div>
                ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Payments ---------------- */
function PaymentsReport() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reqRef = useRef(0);
  const load = useCallback(async () => {
    const myId = ++reqRef.current;
    setLoading(true);
    try {
      const res = await api("GET", `/reports/payments?date_from=${from}&date_to=${to}`);
      if (myId === reqRef.current) setData(res);   // ignore stale/out-of-order responses
    } finally {
      if (myId === reqRef.current) setLoading(false);
    }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="report-payments" className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm text-slate-600">From<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="payments-from" className="block mt-1 rounded-lg border border-slate-300 px-3 py-2" /></label>
        <label className="text-sm text-slate-600">To<input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="payments-to" className="block mt-1 rounded-lg border border-slate-300 px-3 py-2" /></label>
        <button onClick={() => { setFrom(""); setTo(""); }} data-testid="payments-all-time" className="rounded-full px-3 py-1.5 text-xs font-semibold bg-slate-800 text-white hover:bg-slate-900">All time</button>
      </div>
      {loading || !data ? <p className="text-slate-500 py-8 text-center">Loading payments…</p> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Stat label="Total received" value={money0(data.total)} sub={`${data.count} payments · invoices ${money0(data.legacy_total || 0)} + counter sales ${money0(data.pos_total || 0)}${data.native_total ? ` + in-app ${money0(data.native_total)}` : ""}`} tone="sky" big />
            <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="payments-by-method">
              <p className="font-display font-bold text-slate-900 mb-2.5 text-sm">Payments by method</p>
              <div className="grid grid-cols-2 gap-1.5">
                {Object.entries(data.by_method || {}).sort((a, b) => b[1] - a[1]).map(([m, v]) => (
                  <div key={m} className="flex items-center justify-between rounded-lg bg-slate-50 px-2.5 py-1.5" data-testid={`pay-method-${m}`}>
                    <span className="text-[13px] text-slate-600 truncate">{m}</span>
                    <span className="text-[13px] font-bold text-slate-900 ml-2 whitespace-nowrap">{money0(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <ReportExportBar testid="payments" title={`Payments Received ${from} to ${to}`}
            columns={[{ key: "at", label: "Date" }, { key: "sale_number", label: "Invoice" }, { key: "client_name", label: "Client" }, { key: "method", label: "Method" }, { key: "amount", label: "Amount", money: true }]}
            rows={(data.payments || []).map((p) => ({ ...p, at: String(p.at).slice(0, 10) }))} />
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400"><tr>
                <th className="text-left px-4 py-2.5">Date</th><th className="text-left px-4 py-2.5">Invoice</th>
                <th className="text-left px-4 py-2.5">Client</th><th className="text-left px-4 py-2.5">Method</th>
                <th className="text-right px-4 py-2.5">Amount</th></tr></thead>
              <tbody>
                {data.payments.length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No payments in range.</td></tr> :
                  data.payments.map((p, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-4 py-2.5 text-slate-500">{String(p.at).slice(0, 10)}</td>
                      <td className="px-4 py-2.5 font-semibold text-slate-700">{refNo("invoice", p.sale_number)}</td>
                      <td className="px-4 py-2.5 text-slate-600 truncate max-w-[200px]">{p.client_name}</td>
                      <td className="px-4 py-2.5 text-slate-600">{p.method}</td>
                      <td className="px-4 py-2.5 text-right font-extrabold text-emerald-600">{money(p.amount)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {data.count > (data.payments || []).length && (
              <p className="px-4 py-2 text-[12px] text-slate-400 border-t border-slate-100" data-testid="payments-list-cap">Showing the latest {(data.payments || []).length} of {data.count} payments in this range. The Total received above reflects all {data.count}.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Spa Sales History ---------------- */
function SpaSalesReport({ onOpenClient }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [waitingOnly, setWaitingOnly] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = [];
      if (from) qs.push(`date_from=${from}`);
      if (to) qs.push(`date_to=${to}`);
      setData(await api("GET", `/reports/spa-sales${qs.length ? "?" + qs.join("&") : ""}`));
    } finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <p className="text-slate-500 py-8 text-center" data-testid="spa-sales-loading">Loading spa sales…</p>;
  const rows = waitingOnly ? data.waiting : data.rows;
  return (
    <div data-testid="report-spa-sales" className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm text-slate-600">From<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="spa-sales-from" className="block mt-1 rounded-lg border border-slate-300 px-3 py-2" /></label>
        <label className="text-sm text-slate-600">To<input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="spa-sales-to" className="block mt-1 rounded-lg border border-slate-300 px-3 py-2" /></label>
        <button onClick={() => { setFrom(""); setTo(""); }} className="rounded-full px-3 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700">All time</button>
        <label className="ml-2 inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none" data-testid="spa-sales-waiting-toggle">
          <input type="checkbox" checked={waitingOnly} onChange={(e) => setWaitingOnly(e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
          Waiting delivery only
        </label>
        <button onClick={load} className="ml-auto inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </div>
      <ReportExportBar testid="spa-sales" title="Spa Sales Report"
        columns={[{ key: "date", label: "Date" }, { key: "jnumber", label: "J#" }, { key: "client_name", label: "Client" }, { key: "salesperson", label: "Salesperson" }, { key: "status", label: "Status" }, { key: "nett", label: "Nett", money: true }, { key: "balance_due", label: "Balance", money: true }]}
        rows={rows || []} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Spas sold" value={data.count.toLocaleString()} sub="in range" tone="sky" big />
        <Stat label="Total value" value={money0(data.total)} tone="emerald" />
        <Stat label="Waiting delivery" value={data.waiting_count} sub={money0(data.waiting_total)} tone="amber" />
        <Stat label="Years on record" value={data.by_year.length} tone="slate" />
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="font-display font-bold text-slate-900 mb-3">By salesperson</p>
          {data.by_salesperson.slice(0, 8).map((p, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
              <span className="text-slate-600 truncate max-w-[220px]">{p.name}</span><span className="font-semibold text-slate-900">{money0(p.total)}</span>
            </div>
          ))}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="font-display font-bold text-slate-900 mb-3">By year</p>
          <div className="max-h-52 overflow-y-auto">
            {data.by_year.map((y, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                <span className="text-slate-600">{y.year}</span><span className="font-semibold text-slate-900">{money0(y.total)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400"><tr>
            <th className="text-left px-4 py-2.5">Date</th><th className="text-left px-4 py-2.5">J #</th>
            <th className="text-left px-4 py-2.5">Client</th><th className="text-left px-4 py-2.5">Salesperson</th>
            <th className="text-left px-4 py-2.5">Status</th><th className="text-right px-4 py-2.5">Amount</th>
            <th className="text-right px-4 py-2.5">Owing</th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No spa sales in range.</td></tr> :
              rows.map((r, i) => (
                <tr key={i} onClick={() => r.client_id && onOpenClient(r.client_id)} data-testid={`spa-sale-${i}`} className={`border-t border-slate-100 ${r.client_id ? "hover:bg-slate-50 cursor-pointer" : ""}`}>
                  <td className="px-4 py-2.5 text-slate-500">{String(r.date).slice(0, 10)}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{r.jnumber || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-700 truncate max-w-[220px]">{r.client_name}</td>
                  <td className="px-4 py-2.5 text-slate-500 truncate max-w-[140px]">{r.salesperson}</td>
                  <td className="px-4 py-2.5"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${r.status === "Sold Waiting Delivery" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{r.status}</span></td>
                  <td className="px-4 py-2.5 text-right font-extrabold text-slate-900">{money(r.nett)}</td>
                  <td className={`px-4 py-2.5 text-right ${r.balance_due > 0 ? "text-rose-600 font-bold" : "text-slate-300"}`}>{r.balance_due > 0 ? money(r.balance_due) : "—"}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Stock Levels ---------------- */
function StockReport() {
  const [q, setQ] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api("GET", `/reports/stock?low_only=${lowOnly}${q ? `&q=${encodeURIComponent(q)}` : ""}`)); }
    finally { setLoading(false); }
  }, [q, lowOnly]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  return (
    <div data-testid="report-stock" className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product, SKU, category, brand…" data-testid="stock-search" className="flex-1 min-w-[220px] rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none" data-testid="stock-low-toggle">
          <input type="checkbox" checked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
          Low / out of stock only
        </label>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </div>
      {loading || !data ? <p className="text-slate-500 py-8 text-center">Loading stock…</p> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Active SKUs" value={data.total_skus.toLocaleString()} sub={`${data.total_units.toLocaleString()} units on hand`} tone="slate" big />
            <Stat label="Stock value (cost)" value={money0(data.total_value)} tone="emerald" />
            <Stat label="Low stock" value={data.low_count} tone="amber" />
            <Stat label="Out of stock" value={data.out_count} tone="rose" />
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400"><tr>
                <th className="text-left px-4 py-2.5">Product</th><th className="text-left px-4 py-2.5">SKU</th>
                <th className="text-left px-4 py-2.5">Category</th><th className="text-right px-4 py-2.5">Qty</th>
                <th className="text-right px-4 py-2.5">Alert</th><th className="text-right px-4 py-2.5">Price</th>
                <th className="text-right px-4 py-2.5">Value</th><th className="text-left px-4 py-2.5">Status</th></tr></thead>
              <tbody>
                {data.items.length === 0 ? <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">No products found.</td></tr> :
                  data.items.map((p, i) => (
                    <tr key={p.id || i} data-testid={`stock-item-${i}`} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-2.5 text-slate-700 truncate max-w-[280px]">{p.showroom && <span className="text-[10px] font-bold text-sky-600 mr-1">◆</span>}{p.name}</td>
                      <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{p.sku}</td>
                      <td className="px-4 py-2.5 text-slate-500 truncate max-w-[160px]">{p.category || "—"}</td>
                      <td className={`px-4 py-2.5 text-right font-bold ${p.out ? "text-rose-600" : p.low ? "text-amber-600" : "text-slate-800"}`}>{p.qty}</td>
                      <td className="px-4 py-2.5 text-right text-slate-400">{p.stock_alert || "—"}</td>
                      <td className="px-4 py-2.5 text-right text-slate-500">{money(p.price)}</td>
                      <td className="px-4 py-2.5 text-right text-slate-600">{money(p.value)}</td>
                      <td className="px-4 py-2.5">{p.out ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">Out</span> : p.low ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Low</span> : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">In stock</span>}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default function Reports({ view = "debtors", branding, onOpenClient }) {
  const [tabId, scopeParam] = String(view).split(":");
  const [active, setActive] = useState(tabId);
  useEffect(() => { setActive(tabId); }, [tabId]);
  const scope = active === tabId ? (scopeParam || "all") : "all";

  return (
    <div data-testid="reports-panel" className="space-y-5">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">Reports</h1>
        <p className="text-sm text-slate-500">Who owes you money, what you've sold, and what's been paid.</p>
      </div>
      <div className="flex flex-wrap gap-2.5">
        {VIEWS.map((v) => {
          const on = active === v.id;
          const tones = { rose: on ? "bg-rose-600 text-white border-rose-600" : "text-rose-700 border-rose-200 bg-rose-50", emerald: on ? "bg-emerald-600 text-white border-emerald-600" : "text-emerald-700 border-emerald-200 bg-emerald-50", sky: on ? "bg-sky-600 text-white border-sky-600" : "text-sky-700 border-sky-200 bg-sky-50", amber: on ? "bg-amber-600 text-white border-amber-600" : "text-amber-700 border-amber-200 bg-amber-50" };
          return (
            <motion.button whileTap={{ scale: 0.97 }} key={v.id} onClick={() => setActive(v.id)} data-testid={`report-tab-${v.id}`}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold border ${tones[v.tone]}`}>
              <v.icon className="h-4 w-4" /> {v.label}
            </motion.button>
          );
        })}
      </div>
      {active === "debtors" && <DebtorsReport onOpenClient={onOpenClient} branding={branding} scope={scope} />}
      {active === "sales" && <SalesSummary />}
      {active === "spa_sales" && <SpaSalesReport onOpenClient={onOpenClient} />}
      {active === "stock" && <StockReport />}
      {active === "payments" && <PaymentsReport />}
    </div>
  );
}
